import { LoadingOverlay } from "@/components/LoadingOverlay";
import { Switch } from "@/components/ui/switch";
import { showApiError, showSuccess } from "@/lib/toast";
import { addAppUserRole, deleteAppUserRole, getAppUserRole, getRoles } from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Props {
  userId: number;
}

export const UpdateUserRights: React.FC<Props> = ({ userId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["getRoles"],
    queryFn: () =>
      getRoles({
        numberOfObjectsPerPage: 1000,
        pageNumber: 1,
      }),
  });

  const { data: userRolesData, isLoading: userRolesLoading } = useQuery({
    queryKey: ["getAppUserRole", userId],
    queryFn: () => getAppUserRole(userId),
  });

  const addAppUserRoleMutation = useMutation({
    mutationFn: (roleId: number) => addAppUserRole(userId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAppUserRole", userId] });
      queryClient.invalidateQueries({ queryKey: ["getUser"] });
      showSuccess(t("common.assignedSuccess"));
    },
    onError: showApiError,
  });

  const deleteAppUserRoleMutation = useMutation({
    mutationFn: (userRoleId: number) => deleteAppUserRole(userRoleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAppUserRole", userId] });
      queryClient.invalidateQueries({ queryKey: ["getUser"] });
      showSuccess(t("common.unassignedSuccess"));
    },
    onError: showApiError,
  });

  const roles = rolesData?.data.result || [];
  const userRoles = userRolesData?.data.result || [];

  return (
    <LoadingOverlay
      isLoading={
        rolesLoading || userRolesLoading || addAppUserRoleMutation.isPending || deleteAppUserRoleMutation.isPending
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {roles.map((role) => {
          const roleRelation = userRoles.find((userRole) => userRole.role_Id === role.id);

          const isRoleSelected = !!roleRelation;

          return (
            <div key={role.id} className="flex items-center justify-between p-2 border-b">
              <div className="font-medium">{role.roleName}</div>
              <Switch
                checked={isRoleSelected}
                onCheckedChange={(newChecked) => {
                  if (newChecked) {
                    addAppUserRoleMutation.mutate(role.id);
                  } else if (roleRelation) {
                    deleteAppUserRoleMutation.mutate(roleRelation.id);
                  }
                }}
              />
            </div>
          );
        })}
      </div>
    </LoadingOverlay>
  );
};
